<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <h2>Costume Inventory</h2>
            <v-btn to="/iabf/admin/borrowed-tutus" class="mb-3">
              ← Borrowed Tutus
            </v-btn>
            <v-btn to="/iabf/admin/inventory" class="mb-3 ml-2">
              Festival inventory
            </v-btn>
            <v-btn color="primary" class="mb-3 ml-2" @click="openAdd">
              + Add Costume
            </v-btn>
            <v-btn class="mb-3 ml-2" @click="openImport">
              Upload CSV
            </v-btn>
            <v-btn text small class="mb-3 ml-1" @click="downloadTemplate">
              Download Template
            </v-btn>

            <vue-good-table
              :columns="columns"
              :rows="inventory"
              :line-numbers="true"
              :search-options="{ enabled: true, placeholder: 'Search costumes' }"
            >
              <div slot="emptystate">No costumes in inventory yet.</div>
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'status'">
                  <v-chip
                    x-small
                    :color="props.row.status === 'available' ? 'green lighten-4' : 'grey lighten-3'"
                    :text-color="props.row.status === 'available' ? 'green darken-3' : 'grey darken-1'"
                  >
                    {{ props.row.status === 'available' ? 'Available' : 'Assigned' }}
                  </v-chip>
                </span>
                <span v-else-if="props.column.field === 'actions'">
                  <v-btn small text @click="openEdit(props.row)">Edit</v-btn>
                  <v-btn small text color="error" @click="confirmDelete(props.row)">Delete</v-btn>
                </span>
                <span v-else-if="props.column.field === 'measurements'">
                  {{ formatMeasurements(props.row) }}
                </span>
                <span v-else>{{ props.formattedRow[props.column.field] }}</span>
              </template>
            </vue-good-table>
          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>

    <!-- Add / Edit dialog -->
    <v-dialog v-model="dialog" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ editingId ? 'Edit Costume' : 'Add Costume' }}</v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-text-field
              v-model="form.name"
              label="Name / Label *"
              required
              :rules="[v => !!v || 'Required']"
            ></v-text-field>

            <v-select
              v-model="form.type"
              label="Type *"
              :items="typeOptions"
              required
              :rules="[v => !!v || 'Required']"
            ></v-select>

            <v-select
              v-model="form.color"
              label="Color *"
              :items="colorOptions"
              required
              :rules="[v => !!v || 'Required']"
            ></v-select>

            <div class="subtitle-1 mt-3 mb-1">Measurements (inches)</div>
            <v-layout wrap>
              <v-flex xs6 sm4 class="pr-2">
                <v-text-field v-model.number="form.bust" label="Bust" type="number" step="0.1"></v-text-field>
              </v-flex>
              <v-flex xs6 sm4 class="pr-2">
                <v-text-field v-model.number="form.waist" label="Waist" type="number" step="0.1"></v-text-field>
              </v-flex>
              <v-flex xs6 sm4 class="pr-2">
                <v-text-field v-model.number="form.hip" label="Hip" type="number" step="0.1"></v-text-field>
              </v-flex>
              <v-flex xs6 sm4 class="pr-2">
                <v-text-field v-model.number="form.halfGirth" label="½ Girth" type="number" step="0.1"></v-text-field>
              </v-flex>
              <v-flex xs6 sm4 class="pr-2">
                <v-text-field v-model.number="form.girth" label="Girth" type="number" step="0.1"></v-text-field>
              </v-flex>
              <v-flex xs6 sm4>
                <v-text-field v-model.number="form.height" label="Height" type="number" step="0.1"></v-text-field>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CSV Import dialog -->
    <v-dialog v-model="importDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>Upload Costumes from CSV</v-card-title>
        <v-card-text>
          <p class="mb-1">CSV must have columns: <strong>Name, Type, Color</strong> (required) and optionally <strong>Bust, Waist, Hip, Half Girth, Girth, Height</strong> (inches).</p>
          <p class="mb-3">Valid types: Pancake, Pancake with Bodice, Romantic. Valid colors: White, Blue, Pink, Yellow, Purple, Green.</p>
          <v-file-input
            v-model="importFile"
            label="Select CSV file"
            accept=".csv"
            prepend-icon="mdi-file-delimited"
            outlined
            dense
            hide-details
            @change="onFileChange"
          ></v-file-input>

          <div v-if="importError" class="error--text mt-2">{{ importError }}</div>

          <div v-if="importRows.length > 0" class="mt-4">
            <div class="mb-2">
              <strong>{{ importRows.filter(r => r.valid).length }}</strong> valid,
              <strong class="error--text">{{ importRows.filter(r => !r.valid).length }}</strong> invalid
            </div>
            <div style="max-height: 320px; overflow-y: auto; border: 1px solid #e8e5e1;">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Color</th>
                    <th>Measurements</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in importRows"
                    :key="row.rowNum"
                    :style="row.valid ? {} : { background: '#fff3f3' }"
                  >
                    <td>{{ row.rowNum }}</td>
                    <td>{{ row.name }}</td>
                    <td>{{ row.type }}</td>
                    <td>{{ row.color }}</td>
                    <td style="font-size: 0.85em;">{{ formatImportMeasurements(row) }}</td>
                    <td>
                      <span v-if="row.valid" class="success--text">✓</span>
                      <span v-else class="error--text" style="font-size: 0.85em;">{{ row.errors.join('; ') }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeImport">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="importing"
            :disabled="importRows.filter(r => r.valid).length === 0"
            @click="doImport"
          >
            Import {{ importRows.filter(r => r.valid).length }} Costume{{ importRows.filter(r => r.valid).length !== 1 ? 's' : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Delete Costume?</v-card-title>
        <v-card-text>Are you sure you want to delete <strong>{{ deletingItem && deletingItem.name }}</strong>? This cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style>
  h2 {
    font-family: Quattrocento;
    font-weight: 400;
    font-size: 39px;
    letter-spacing: -.02em;
    line-height: 1em;
  }
  .vgt-table {
    font-family: Fanwood Text;
  }
  .vgt-table thead {
    font-size: 1.2em !important;
  }
</style>

<script>
import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table'

const emptyForm = () => ({
  name: '',
  type: '',
  color: '',
  bust: null,
  waist: null,
  hip: null,
  halfGirth: null,
  girth: null,
  height: null
})

export default {
  components: { VueGoodTable },
  data () {
    return {
      columns: [
        { label: 'Status', field: 'status', width: '110px' },
        { label: 'Name', field: 'name' },
        { label: 'Type', field: 'type' },
        { label: 'Color', field: 'color' },
        { label: 'Measurements (in)', field: 'measurements', sortable: false },
        { label: '', field: 'actions', sortable: false, width: '140px' }
      ],
      typeOptions: [
        { text: 'Pancake', value: 'Pancake' },
        { text: 'Pancake with Bodice', value: 'Pancake with Bodice' },
        { text: 'Romantic', value: 'Romantic' }
      ],
      colorOptions: ['White', 'Blue', 'Pink', 'Yellow', 'Purple', 'Green'],
      dialog: false,
      deleteDialog: false,
      editingId: null,
      deletingItem: null,
      form: emptyForm(),
      saving: false,
      deleting: false,
      importDialog: false,
      importFile: null,
      importRows: [],
      importError: '',
      importing: false
    }
  },
  computed: {
    assignedTutuNames () {
      const names = new Set()
      const dancers = this.$store.getters.loadedIABFDancers || []
      dancers.forEach(d => {
        if (d.borrowedWaltzTutu) names.add(d.borrowedWaltzTutu)
        if (d.borrowedAliceTutu) names.add(d.borrowedAliceTutu)
        if (d.borrowedGarlandTutu) names.add(d.borrowedGarlandTutu)
      })
      return names
    },
    inventory () {
      return this.$store.getters.costumeInventory.map(item => ({
        ...item,
        status: this.assignedTutuNames.has(item.name) ? 'assigned' : 'available'
      }))
    }
  },
  created () {
    this.$store.dispatch('loadCostumeInventory')
    this.$store.dispatch('loadIABFDancers')
  },
  methods: {
    formatMeasurements (row) {
      const parts = [
        { label: 'B', val: row.bust },
        { label: 'W', val: row.waist },
        { label: 'H', val: row.hip },
        { label: '½G', val: row.halfGirth },
        { label: 'G', val: row.girth },
        { label: 'Ht', val: row.height }
      ].filter(p => p.val)
      return parts.map(p => p.label + ': ' + p.val).join(' / ')
    },
    openAdd () {
      this.editingId = null
      this.form = emptyForm()
      this.dialog = true
    },
    openEdit (row) {
      this.editingId = row.id
      this.form = {
        name: row.name || '',
        type: row.type || '',
        color: row.color || '',
        bust: row.bust || null,
        waist: row.waist || null,
        hip: row.hip || null,
        halfGirth: row.halfGirth || null,
        girth: row.girth || null,
        height: row.height || null
      }
      this.dialog = true
    },
    closeDialog () {
      this.dialog = false
      this.$refs.form && this.$refs.form.resetValidation()
    },
    async save () {
      if (!this.$refs.form.validate()) return
      this.saving = true
      const payload = { ...this.form, id: this.editingId }
      await this.$store.dispatch('saveCostumeInventoryItem', payload)
      if (this.editingId) {
        await this.$store.dispatch('loadCostumeInventory')
      }
      this.saving = false
      this.closeDialog()
    },
    confirmDelete (row) {
      this.deletingItem = row
      this.deleteDialog = true
    },
    async doDelete () {
      this.deleting = true
      await this.$store.dispatch('deleteCostumeInventoryItem', this.deletingItem.id)
      this.deleting = false
      this.deleteDialog = false
      this.deletingItem = null
    },
    openImport () {
      this.importFile = null
      this.importRows = []
      this.importError = ''
      this.importDialog = true
    },
    closeImport () {
      this.importDialog = false
    },
    onFileChange (file) {
      this.importRows = []
      this.importError = ''
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        this.importRows = this.parseCSV(e.target.result)
      }
      reader.readAsText(file)
    },
    splitCSVLine (line) {
      const result = []
      let current = ''
      let inQuotes = false
      for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (ch === '"') {
          inQuotes = !inQuotes
        } else if (ch === ',' && !inQuotes) {
          result.push(current.trim())
          current = ''
        } else {
          current += ch
        }
      }
      result.push(current.trim())
      return result
    },
    parseCSV (text) {
      const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n').filter(l => l.trim())
      if (lines.length < 2) {
        this.importError = 'File must have a header row and at least one data row.'
        return []
      }
      const headers = this.splitCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s*\([^)]*\)/g, '').trim())
      const idx = (...names) => { for (const n of names) { const i = headers.indexOf(n); if (i !== -1) return i } return -1 }
      const nameIdx = idx('name')
      const typeIdx = idx('type')
      const colorIdx = idx('color')
      const bustIdx = idx('bust')
      const waistIdx = idx('waist')
      const hipIdx = idx('hip')
      const halfGirthIdx = idx('half girth', '1/2 girth', 'half-girth')
      const girthIdx = idx('girth')
      const heightIdx = idx('height')

      if (nameIdx === -1 || typeIdx === -1 || colorIdx === -1) {
        this.importError = 'CSV must have Name, Type, and Color header columns.'
        return []
      }

      const validTypes = ['Pancake', 'Pancake with Bodice', 'Romantic']
      const validColors = ['White', 'Blue', 'Pink', 'Yellow', 'Purple', 'Green']

      return lines.slice(1).map((line, i) => {
        const cols = this.splitCSVLine(line)
        const errors = []
        const name = cols[nameIdx] || ''
        const type = cols[typeIdx] || ''
        const color = cols[colorIdx] || ''
        if (!name) errors.push('Name required')
        if (!type) errors.push('Type required')
        else if (!validTypes.includes(type)) errors.push('Type must be: ' + validTypes.join(', '))
        if (!color) errors.push('Color required')
        else if (!validColors.includes(color)) errors.push('Color must be: ' + validColors.join(', '))

        const parseNum = (colIdx, label) => {
          if (colIdx === -1 || !cols[colIdx] || !cols[colIdx].trim()) return null
          const n = parseFloat(cols[colIdx])
          if (isNaN(n)) { errors.push(label + ' must be a number'); return null }
          return n
        }

        return {
          rowNum: i + 2,
          name,
          type,
          color,
          bust: parseNum(bustIdx, 'Bust'),
          waist: parseNum(waistIdx, 'Waist'),
          hip: parseNum(hipIdx, 'Hip'),
          halfGirth: parseNum(halfGirthIdx, 'Half Girth'),
          girth: parseNum(girthIdx, 'Girth'),
          height: parseNum(heightIdx, 'Height'),
          errors,
          valid: errors.length === 0
        }
      })
    },
    formatImportMeasurements (row) {
      return [
        row.bust ? 'B: ' + row.bust : null,
        row.waist ? 'W: ' + row.waist : null,
        row.hip ? 'H: ' + row.hip : null,
        row.halfGirth ? '½G: ' + row.halfGirth : null,
        row.girth ? 'G: ' + row.girth : null,
        row.height ? 'Ht: ' + row.height : null
      ].filter(Boolean).join(' / ')
    },
    async doImport () {
      const valid = this.importRows.filter(r => r.valid)
      if (!valid.length) return
      this.importing = true
      await this.$store.dispatch('bulkSaveCostumeInventoryItems', valid)
      this.importing = false
      this.closeImport()
    },
    downloadTemplate () {
      const header = 'Name,Type,Color,Bust,Waist,Hip,Half Girth,Girth,Height'
      const example = 'Tutu A,Pancake with Bodice,Blue,34,26,36,,42,'
      const csv = header + '\n' + example + '\n'
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'costume-inventory-template.csv'
      a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>
